/**
 * Look-ahead scheduler (W1-14, A§8, R§5.4, R§6.4, D§5.9).
 *
 * A 25 ms tick from a worker timer (background-tab throttling does not starve it) plus every animation frame call
 * `reconcile`, which makes the set of scheduled voices equal to "every emission (and metronome click) whose start
 * falls in [now - 50 ms, now + 120 ms)":
 *  - for each timeline segment the context window is converted to a chart window and the emission source is asked
 *    for `[t0, t1)` (half-open, so adjacent windows never return an emission twice);
 *  - each emission with a sound gets `when = ctxStart + (time + shift - posStart) / rate + delay(type)`, the exact
 *    start time (per-type delays: Tap/Flick vs Drag by the note kind, RPE semantics, also for custom sounds);
 *  - an item is one OCCURRENCE: the emission (note, kind, time, sound) or metronome click within the segment's play
 *    run and loop lap (timeline.ts). A seek or a new play starts a new run, a loop wrap a new lap, so repetitions
 *    are distinct occurrences, while rate splices, offset and delay changes and media-element re-anchors keep them;
 *  - a pending occurrence whose `when` moved (offset or delay change, rate change, re-anchor) is cancelled and
 *    scheduled again at the new time; one that has STARTED is never started again, whatever its new `when` (it is
 *    remembered for FORGET_AFTER_SEC, and while the element path holds for its clock);
 *  - a pending voice whose occurrence no longer exists is cancelled (pause, seek, mute, edits); sounding voices ring
 *    out.
 * Hitsounds are skipped while the hitsound volume is at most 0.01 (Phira's `volumeSfx` rule, R§5.4).
 */

import type { Emission } from '../core/engine/types';
import type { Sec } from '../core/time/types';
import type { EmissionSource } from './types';
import type { Metronome, ClickSounds } from './metronome';
import { soundKey, type SoundStore, type Voice, type VoiceContext, type VoicePool } from './hitsoundBank';
import { isProvisional, type Segment } from './timeline';

/** Scheduling horizon (A§8). */
export const SCHEDULE_HORIZON_SEC = 0.12;
/** Tick period of the worker timer (A§8). */
export const SCHEDULE_TICK_MS = 25;
/** Emissions up to this late are still started (immediately): a delayed tick, a negative delay at play start. */
export const LATE_TOLERANCE_SEC = 0.05;
/** Two computations of the same item's start agree within this. */
const SAME_WHEN_SEC = 1e-4;
/**
 * Started occurrences are remembered this long (context seconds after their start), so a mapping change that moves
 * them (an offset change at a low rate moves `when` by delta / rate, a hitsound delay by up to 500 ms) cannot start
 * them again.
 */
export const FORGET_AFTER_SEC = 2;

export interface Delays {
  /** Seconds added to Tap/Flick (and custom) hitsounds. */
  readonly tapFlick: number;
  /** Seconds added to Drag hitsounds. */
  readonly drag: number;
}

export interface ScheduleInput {
  readonly ctx: VoiceContext;
  /** The context's currentTime. */
  readonly now: number;
  /** Timeline segments of the current play run (empty while paused). */
  readonly segments: readonly Segment[];
  /** Music seconds minus chart seconds. */
  readonly shift: number;
  readonly hitBus: AudioNode;
  readonly clickBus: AudioNode;
}

interface Item {
  readonly when: number;
  readonly voice: Voice;
  readonly pool: VoicePool;
}

/** Whether the item's voice has started (or starts within the current render quantum). */
function started(it: Item, now: number): boolean {
  return it.voice.when <= now;
}

export interface SchedulerDeps {
  readonly sounds: SoundStore;
  readonly hitPool: VoicePool;
  readonly clickPool: VoicePool;
  readonly metronome: Metronome;
  readonly clicks: () => ClickSounds | null;
}

export class Scheduler {
  private readonly deps: SchedulerDeps;
  private source: EmissionSource | null = null;
  private delays: Delays = { tapFlick: 0, drag: 0 };
  private hitsoundsOn = true;
  /** One item per occurrence key (`run:lap|...`). */
  private readonly items = new Map<string, Item>();

  constructor(deps: SchedulerDeps) {
    this.deps = deps;
  }

  setSource(src: EmissionSource | null): void {
    this.source = src;
  }

  setDelays(d: Delays): void {
    this.delays = { tapFlick: finiteOr0(d.tapFlick), drag: finiteOr0(d.drag) };
  }

  /** Hitsounds at (or below) 1 % volume are not scheduled at all. */
  setHitsoundsAudible(on: boolean): void {
    this.hitsoundsOn = on;
  }

  /** Items currently tracked (scheduled or recently played). */
  get trackedCount(): number {
    return this.items.size;
  }

  /** Per-type hitsound delay of an emission: Drag notes use the Drag delay, every other kind Tap/Flick. */
  private delayOf(e: Emission): number {
    return e.noteKind === 'drag' ? this.delays.drag : this.delays.tapFlick;
  }

  reconcile(input: ScheduleInput): void {
    const { now } = input;
    const seen = new Set<Item>();
    const lo = now - LATE_TOLERANCE_SEC;
    const hi = now + SCHEDULE_HORIZON_SEC;
    const dMin = Math.min(0, this.delays.tapFlick, this.delays.drag);
    const dMax = Math.max(0, this.delays.tapFlick, this.delays.drag);
    const src = this.hitsoundsOn ? this.source : null;
    const metronome = this.deps.metronome.enabled ? this.deps.metronome : null;
    const clicks = metronome === null ? null : this.deps.clicks();

    for (const seg of input.segments) {
      if (!Number.isFinite(seg.ctxStart)) continue;
      const occurrence = `${seg.run}:${seg.lap}|`;
      const toChart = (ctxT: number): Sec => seg.posStart + (ctxT - seg.ctxStart) * seg.rate - input.shift;
      const toWhen = (chartT: Sec): number => seg.ctxStart + (chartT + input.shift - seg.posStart) / seg.rate;

      if (src !== null) {
        const a = Math.max(seg.ctxStart, lo - dMax);
        const b = Math.min(seg.ctxEnd, hi - dMin);
        if (b > a) {
          const c0 = toChart(a);
          const c1 = toChart(b);
          for (const e of src(c0, c1, seg.rate)) {
            if (e.sound === null || !(e.time >= c0 && e.time < c1)) continue;
            const when = toWhen(e.time) + this.delayOf(e);
            if (when < lo || when >= hi) continue;
            const key = `${occurrence}h|${e.note}|${e.kind}|${e.time}|${soundKey(e.sound)}`;
            seen.add(this.ensure(input, key, when, this.deps.sounds.resolve(e.sound), input.hitBus, this.deps.hitPool));
          }
        }
      }

      if (metronome !== null && clicks !== null) {
        const a = Math.max(seg.ctxStart, lo);
        const b = Math.min(seg.ctxEnd, hi);
        if (b > a) {
          for (const c of metronome.clicksIn(toChart(a), toChart(b))) {
            const when = toWhen(c.t);
            if (when < lo || when >= hi) continue;
            const key = `${occurrence}m|${c.t}|${c.accent ? 1 : 0}`;
            seen.add(this.ensure(input, key, when, clicks.of(c.accent), input.clickBus, this.deps.clickPool));
          }
        }
      }
    }

    // Cancel pending items that are no longer wanted; forget old started ones. While the element path waits for its
    // clock (a provisional segment), nothing is scheduled and the music position does not advance: keep them all.
    const holding = input.segments.some(isProvisional);
    for (const [key, it] of this.items) {
      if (seen.has(it)) continue;
      if (!started(it, now)) {
        it.pool.release(it.voice, now);
        this.items.delete(key);
      } else if (!holding && it.voice.when <= now - FORGET_AFTER_SEC) {
        this.items.delete(key);
      }
    }
  }

  /**
   * The item of occurrence `key` at `when`: a started item stays as it is (never started twice), a pending one at the
   * same time is kept, a pending one at another time is cancelled and scheduled again.
   */
  private ensure(
    input: ScheduleInput,
    key: string,
    when: number,
    buffer: AudioBuffer,
    bus: AudioNode,
    pool: VoicePool,
  ): Item {
    const prev = this.items.get(key);
    if (prev !== undefined) {
      if (started(prev, input.now)) return prev;
      if (Math.abs(prev.when - when) <= SAME_WHEN_SEC && prev.pool === pool) return prev;
      prev.pool.release(prev.voice, input.now);
    }
    const voice = pool.acquire(input.ctx, buffer, when, bus, input.now);
    const item: Item = { when, voice, pool };
    this.items.set(key, item);
    return item;
  }

  /**
   * Cancels every pending item (sounds replaced, dispose). Items that already started stay tracked, so the next
   * pass does not start them again.
   */
  cancelAll(now: number): void {
    for (const [key, it] of this.items) {
      if (started(it, now)) continue;
      it.pool.release(it.voice, now);
      this.items.delete(key);
    }
  }
}

function finiteOr0(x: number): number {
  return Number.isFinite(x) ? x : 0;
}

// ---------------------------------------------------------------------------------------------------------------
// Tick sources
// ---------------------------------------------------------------------------------------------------------------

export interface Ticker {
  start(): void;
  stop(): void;
  dispose(): void;
}

/** Inline worker: setInterval inside a worker keeps ticking in background tabs (main-thread timers get throttled). */
const TICK_WORKER_SOURCE =
  'let id=null;onmessage=function(e){var d=e.data;if(d&&d.type==="start"){if(id!==null)clearInterval(id);' +
  'id=setInterval(function(){postMessage(0);},d.ms);}else if(d&&d.type==="stop"){if(id!==null)clearInterval(id);id=null;}};';

export interface TickerEnv {
  readonly Worker?: typeof Worker;
  readonly Blob?: typeof Blob;
  readonly URL?: Pick<typeof URL, 'createObjectURL' | 'revokeObjectURL'>;
  readonly setInterval: (cb: () => void, ms: number) => unknown;
  readonly clearInterval: (id: unknown) => void;
}

function defaultTickerEnv(): TickerEnv {
  const g = globalThis as {
    Worker?: typeof Worker;
    Blob?: typeof Blob;
    URL?: typeof URL;
  };
  const env: TickerEnv = {
    setInterval: (cb, ms) => setInterval(cb, ms),
    clearInterval: (id) => {
      clearInterval(id as ReturnType<typeof setInterval>);
    },
    ...(g.Worker === undefined ? {} : { Worker: g.Worker }),
    ...(g.Blob === undefined ? {} : { Blob: g.Blob }),
    ...(g.URL === undefined || typeof g.URL.createObjectURL !== 'function' ? {} : { URL: g.URL }),
  };
  return env;
}

/**
 * A periodic tick (default 25 ms) from a worker timer, falling back to a main-thread interval when workers or blob
 * URLs are unavailable (or the worker fails, e.g. under a strict CSP).
 */
export function createTicker(
  onTick: () => void,
  intervalMs = SCHEDULE_TICK_MS,
  env: TickerEnv = defaultTickerEnv(),
): Ticker {
  let worker: Worker | null = null;
  let url: string | null = null;
  let interval: unknown = null;
  let running = false;
  let useFallback = false;

  const startFallback = (): void => {
    if (interval === null) interval = env.setInterval(onTick, intervalMs);
  };
  const stopFallback = (): void => {
    if (interval !== null) env.clearInterval(interval);
    interval = null;
  };
  const dropWorker = (): void => {
    worker?.terminate();
    worker = null;
    if (url !== null) env.URL?.revokeObjectURL(url);
    url = null;
  };
  const ensureWorker = (): Worker | null => {
    if (useFallback) return null;
    if (worker !== null) return worker;
    const W = env.Worker;
    const B = env.Blob;
    const U = env.URL;
    if (W === undefined || B === undefined || U === undefined) {
      useFallback = true;
      return null;
    }
    try {
      url = U.createObjectURL(new B([TICK_WORKER_SOURCE], { type: 'text/javascript' }));
      const w = new W(url);
      w.onmessage = () => {
        if (running) onTick();
      };
      w.onerror = () => {
        useFallback = true;
        dropWorker();
        if (running) startFallback();
      };
      worker = w;
      return w;
    } catch {
      useFallback = true;
      dropWorker();
      return null;
    }
  };

  return {
    start() {
      if (running) return;
      running = true;
      const w = ensureWorker();
      if (w === null) startFallback();
      else w.postMessage({ type: 'start', ms: intervalMs });
    },
    stop() {
      if (!running) return;
      running = false;
      worker?.postMessage({ type: 'stop' });
      stopFallback();
    },
    dispose() {
      running = false;
      stopFallback();
      dropWorker();
    },
  };
}

export interface FrameLoop {
  start(): void;
  stop(): void;
}

export interface RafEnv {
  readonly request: (cb: (t: number) => void) => number;
  readonly cancel: (id: number) => void;
}

export function defaultRafEnv(): RafEnv | null {
  const g = globalThis as {
    requestAnimationFrame?: (cb: (t: number) => void) => number;
    cancelAnimationFrame?: (id: number) => void;
  };
  const req = g.requestAnimationFrame;
  const can = g.cancelAnimationFrame;
  if (typeof req !== 'function' || typeof can !== 'function') return null;
  return {
    request: (cb) => req(cb),
    cancel: (id) => {
      can(id);
    },
  };
}

/** Calls `onFrame` on every animation frame while started (the scheduler's rAF top-up); inert without rAF. */
export function createFrameLoop(onFrame: (t: number) => void, env: RafEnv | null = defaultRafEnv()): FrameLoop {
  let id: number | null = null;
  const loop = (t: number): void => {
    if (env === null) return;
    id = env.request(loop);
    onFrame(t);
  };
  return {
    start() {
      if (env === null || id !== null) return;
      id = env.request(loop);
    },
    stop() {
      if (env !== null && id !== null) env.cancel(id);
      id = null;
    },
  };
}
